import React, { useCallback, useEffect, useMemo } from 'react'
import { Iffy, DNABox } from '@alucio/lux-ui'
import { DNAFolderGridList, useCurrentPage, DNABreadcrumb, TitleBar } from 'src/components/DNA'
import { filterCollection } from 'src/state/redux/selector/common'
import { filters, sorts, merge } from 'src/state/redux/folder/query'
import { isDocumentVersionORM } from 'src/types/types'
import { DisplayModes } from 'src/components/DNA/hooks/useDisplayMode'
import DNAGrid from 'src/components/DNA/Grid/DNAGrid'
import { S, useDNAMyFoldersSharedResources } from './DNAMyFolders'
import { FolderVariants } from './constants';
import format from 'date-fns/format'
import { useSelector } from 'react-redux'
import { userById } from 'src/state/redux/selector/user';
import { RootState } from 'src/state/redux';

/**
 * Hook to get folder creator information and generate dynamic description
 */
const useFolderDescription = (currentFolder: any) => {
  const defaultDescription = 'View and restore archived folders';

  const createdByUserId = useMemo(() => currentFolder?.model?.createdBy, [currentFolder?.model?.createdBy]);
  const createdByUser = useSelector((state: RootState) => userById(state, createdByUserId));
  const createdBy = useMemo(() =>
    createdByUser?.meta?.formattedName || createdByUserId || 'Unknown',
  [createdByUser?.meta?.formattedName, createdByUserId],
  );

  const getFolderDescription = useCallback(() => {
    if (!currentFolder?.model) {
      return defaultDescription;
    }

    const createdAt = currentFolder.model.createdAt
      ? format(new Date(currentFolder.model.createdAt), 'MMM dd, yyyy, h:mm a')
      : 'Unknown date';
    const updatedAt = currentFolder.model.updatedAt
      ? format(new Date(currentFolder.model.updatedAt), 'MMM dd, yyyy, h:mm a')
      : 'Unknown date';

    return `Created by ${createdBy} on ${createdAt} | Last updated: ${updatedAt}`;
  }, [currentFolder?.model, createdBy, defaultDescription]);

  return getFolderDescription;
};

const ArchivedFolders: React.FC = () => {
  const {
    displayMode,
    toggleDisplayMode,
    folderNav,
    folderNavURL,
    onPress,
    emptyVariantType,
  } = useDNAMyFoldersSharedResources();
  const { setDisplaySharedFolders } = folderNav;

  const archivedFolders = filterCollection(
    folderNav.currentFolders,
    merge(filters.archived, filters.rootFolder, sorts.pinnedAsc),
  )
  const route = useCurrentPage({ exact: false })
  const isPublisherRoute = route?.configOptions?.modules?.includes('publisher')
  const archivedFoldersTab = !!route?.PATH.includes('archived_folders')
  const viewerItems =  folderNav.currentItems.filter(
    item => (
      isDocumentVersionORM(item.relations.itemORM)
        ? item.relations.itemORM.model.status !== 'NOT_PUBLISHED'
        : true
    ),
  )
  const items = folderNav.isCurrentlyNested ? isPublisherRoute ? folderNav.currentItems : viewerItems : archivedFolders;
  const gridListVariant = folderNav.isCurrentlyNested
    ? FolderVariants.DEFAULT
    : FolderVariants.ROOT_FOLDER;

  const currentFolder = useMemo(() => folderNav.folderStack.slice(-1)[0], [folderNav.folderStack]);

  const title = useMemo(() =>
    currentFolder?.model.name || 'Archived Folders',
  [currentFolder?.model.name],
  );

  const getFolderDescription = useFolderDescription(currentFolder);

  useEffect(() => {
    if (archivedFoldersTab) setDisplaySharedFolders(false)
  }, [archivedFoldersTab]);

  return (
    <DNABox fill appearance="col">
      {/* Breadcrumb Nav */}
      <Iffy is={folderNav.folderStack.length > 0}>
        <DNABox
          alignY="center"
          spacing="between"
          childFill={0}
          childStyle={[1, { marginLeft: 3 }]}
        >
          <DNABox fill alignY="center">
            <DNABreadcrumb
              rootTitle="Archived Folders"
              folderStack={folderNav.folderStack}
              onRootPress={() => folderNavURL.popToFolder()}
              onFolderPress={(folderORM) => folderNavURL.popToFolder(folderORM)}
              rootTestID="page-title"
            />
          </DNABox>
        </DNABox>
      </Iffy>
      <TitleBar
        title={title}
        description={getFolderDescription()}
        showToggleView={true}
        toggleView={{
          activeView: displayMode,
          onToggle: toggleDisplayMode,
        }}
      />
      {/* Content */}
      <Iffy is={displayMode === DisplayModes.grid}>
        <DNABox style={S.gridViewContainer}>
          <DNAGrid
            items={items}
            onPress={onPress}
            emptyVariant={emptyVariantType}
          />
        </DNABox>
      </Iffy>
      <Iffy is={displayMode === DisplayModes.list}>
        <DNAFolderGridList
          variant={gridListVariant}
          items={items}
          emptyVariant={emptyVariantType}
          onPress={onPress}
        />
      </Iffy>
    </DNABox>
  )
}

export default ArchivedFolders;
